import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import {
  createCaseFingerprintFromRegistry,
  createInitialClinicalRegistry,
  Patient,
} from "./types";
import { auth, db } from "../firebase/client";

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  adjustUsageQuota,
  consumeUsageQuota,
  syncPatientUsageCount,
} from "../services/usageLimits";
import { buildDoctorProfilePayload, normalizeDoctorProfile } from "../utils/doctorProfile";

interface PatientContextType {
  patients: Patient[];
  activePatient: Patient | null;
  addPatient: (patient: Patient) => Promise<void>;
  setActivePatient: (patient: Patient | null) => void;
  updatePatient: (id: string, updates: Partial<Patient>) => Promise<void>;
  deletePatient: (id: string) => Promise<void>;
  loadingPatients: boolean;
}

const PatientContext = createContext<PatientContextType | undefined>(undefined);

export const PatientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [activePatientState, setActivePatientState] = useState<Patient | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const [loadingPatients, setLoadingPatients] = useState(true);

  const normalizePatientRecord = (patient: Patient): Patient => ({
    ...patient,
    comorbidities: Array.isArray(patient.comorbidities) ? patient.comorbidities : [],
    images: Array.isArray(patient.images) ? patient.images : [],
    patientFiles: Array.isArray(patient.patientFiles) ? patient.patientFiles : [],
    allergies: Array.isArray(patient.allergies) ? patient.allergies : [],
    clinicalNotes: Array.isArray(patient.clinicalNotes) ? patient.clinicalNotes : [],
  });

  // Descobrir UID do usuário logado
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
    });

    return () => unsub();
  }, []);

  // Assinar pacientes do Firestore em tempo real
  useEffect(() => {
    if (!uid) {
      setPatients([]);
      setActivePatientState(null);
      setLoadingPatients(false);
      return;
    }

    setLoadingPatients(true);

    const colRef = collection(db, "users", uid, "patients");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) =>
          normalizePatientRecord({
            id: d.id,
            ...(d.data() as any),
          } as Patient)
        );

        setPatients(list);
        void syncPatientUsageCount(uid, list.length).catch((error) => {
          console.error("Failed to sync patient usage count:", error);
        });

        setActivePatientState((prev) => {
          if (!prev) return null;
          const updated = list.find((p) => p.id === prev.id);
          return updated ? normalizePatientRecord(updated) : null;
        });

        setLoadingPatients(false);
      },
      (err) => {
        console.error("Failed to load patients from Firestore:", err);
        setLoadingPatients(false);
      }
    );

    return () => unsub();
  }, [uid]);

  const requireUid = () => {
    if (!uid) {
      throw new Error("Usuário não autenticado (uid ausente).");
    }
    return uid;
  };

  const addPatient = async (patient: Patient) => {
    const userId = requireUid();
    const docRef = doc(db, "users", userId, "patients", patient.id);
    let usageReserved = false;

    const { responsibleDoctor, ...patientSafe } = patient as any;
    const clinicalRegistry = patient.clinicalRegistry ?? createInitialClinicalRegistry();
    const caseFingerprint = createCaseFingerprintFromRegistry(
      clinicalRegistry,
      patient.caseFingerprint
    );
    const userProfileSnap = await getDoc(doc(db, "users", userId));
    const userProfileData = userProfileSnap.exists() ? userProfileSnap.data() : null;
    const normalizedDoctor = normalizeDoctorProfile(userProfileData, userProfileData?.profile, auth.currentUser);
    const persistedDoctor = buildDoctorProfilePayload({
      ...normalizedDoctor,
      fullName: normalizedDoctor.fullName || auth.currentUser?.displayName || "",
      orthoId:
        normalizedDoctor.orthoId ||
        auth.currentUser?.email ||
        userId,
    });

    try {
      await consumeUsageQuota(userId, "patients");
      usageReserved = true;

      await setDoc(
        docRef,
        {
          ...patientSafe,
          clinicalRegistry,
          caseFingerprint,
          dataSharingMode: patient.dataSharingMode ?? "private",
          doctorOrthoId: persistedDoctor.orthoId,
          responsibleDoctorUid: userId,
          responsibleDoctorName: persistedDoctor.fullName,
          responsibleDoctorEmail: persistedDoctor.email,
          responsibleDoctor: {
            name: persistedDoctor.fullName,
            crm: persistedDoctor.crm,
            crmState: persistedDoctor.crmState,
            rqe: persistedDoctor.rqe,
            orthoId: persistedDoctor.orthoId,
            specialty: persistedDoctor.specialty,
            subspecialty: persistedDoctor.subspecialty,
          },
          doctor: {
            name: persistedDoctor.fullName,
            ...persistedDoctor,
          },
          auditTrail: [
            {
              action: "PATIENT_CREATED",
              module: "patients",
              doctorUid: userId,
              doctorName: auth.currentUser?.displayName ?? "",
              timestamp: new Date().toISOString(),
            },
          ],
          createdAt: (patient as any).createdAt ?? serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      if (usageReserved) {
        try {
          await adjustUsageQuota(userId, "patients", -1);
        } catch (rollbackError) {
          console.error("Failed to rollback patient usage quota:", rollbackError);
        }
      }
      throw error;
    }

    setActivePatientState(normalizePatientRecord({
      ...patient,
      clinicalRegistry,
      caseFingerprint,
    }));
  };

  const updatePatient = async (id: string, updates: Partial<Patient>) => {
    const userId = requireUid();
    const docRef = doc(db, "users", userId, "patients", id);
    const currentPatient =
      patients.find((patient) => patient.id === id) ||
      (activePatientState?.id === id ? activePatientState : null);
    const clinicalRegistry =
      updates.clinicalRegistry || currentPatient?.clinicalRegistry;
    const caseFingerprint = createCaseFingerprintFromRegistry(
      clinicalRegistry,
      updates.caseFingerprint || currentPatient?.caseFingerprint
    );
    const payload = {
      ...updates,
      ...(caseFingerprint ? { caseFingerprint } : {}),
      updatedAt: serverTimestamp(),
    };

    await updateDoc(docRef, payload as any);

    // Atualização imediata local para refletir antes do onSnapshot voltar
    setPatients((prev) =>
      prev.map((p) => (p.id === id ? normalizePatientRecord({ ...p, ...payload } as Patient) : p))
    );

    setActivePatientState((prev) => {
      if (!prev || prev.id !== id) return prev;
      return normalizePatientRecord({ ...prev, ...payload } as Patient);
    });
  };

  const deletePatient = async (id: string) => {
    const userId = requireUid();
    const docRef = doc(db, "users", userId, "patients", id);

    await deleteDoc(docRef);
    await adjustUsageQuota(userId, "patients", -1);

    setPatients((prev) => prev.filter((p) => p.id !== id));

    if (activePatientState?.id === id) {
      setActivePatientState(null);
    }
  };

  const setActivePatient = (patient: Patient | null) => {
    setActivePatientState(patient ? normalizePatientRecord(patient) : null);
  };

  const value = useMemo(
    () => ({
      patients,
      activePatient: activePatientState,
      addPatient,
      setActivePatient,
      updatePatient,
      deletePatient,
      loadingPatients,
    }),
    [patients, activePatientState, loadingPatients]
  );

  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>;
};

export const usePatient = () => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error("usePatient must be used within a PatientProvider");
  }
  return context;
};
